import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import "tailwindcss/tailwind.css";
import { Empty, Form } from "antd";
import "../../styles.css";
import Vectorsorting from "../../assets/Vectorsorting.svg"
import ShimmerLoader from "../../loading";
import { useTranslation } from "react-i18next";
import CustomPagination from "../../components/CustomWidgets/CustomPagination";
import { useSelector } from "react-redux";
import Edit from "../../assets/Edit.svg";
import deleteicon from "../../assets/Order/Delete.svg";

const STATUS_STYLES = {
    active:  "bg-green-100 text-green-700",
    expired: "bg-red-100 text-red-600",
    upcoming:"bg-yellow-100 text-yellow-700",
};

const formatDate = (val) => {
    if (!val) return "—";
    // "2026-06-25 15:39:00" → "25 Jun 2026, 15:39"
    const d = new Date(val.replace(" ", "T"));
    return isNaN(d)
        ? val
        : d.toLocaleString("en-GB", {
              day: "2-digit", month: "short", year: "numeric",
              hour: "2-digit", minute: "2-digit", hour12: false,
          });
};

const CashierOfferTable = ({ additionalData, pagination, showEdit, onRowClick, onEditLine, onDelete ,activeTab, totalBill}) => {
    const { t } = useTranslation();
    const [flag, setFlag] = useState(false);
    const [form] = Form.useForm();

    const data = useMemo(() => additionalData || [], [additionalData]);

    useEffect(() => {
        if (additionalData?.length === 0 && !flag) {
            const timer = setTimeout(() => setFlag(true), 3000);
            return () => clearTimeout(timer);
        }
    }, [additionalData, flag]);

    const columns = useMemo(() => [
        {
            Header: t("ID"),
            accessor: "promotion_id",
            Cell: ({ value }) => (
                <span className=" text-[#343C6A]">#{value}</span>
            ),
        },
        {
            Header: t("Name"),
            accessor: "name",
            Cell: ({ value }) => (
                <span className="font-medium text-[#343C6A]">{value || "—"}</span>
            ),
        },
        {
            Header: t("Type"),
            accessor: "promo_offer_type",
            Cell: ({ value }) => {
                const labelMap = {
                    buy_x_get_y:"Buy X Get Y",
                    bill_free_item: "Bill - Free Item",
                    bill_amount_off:"Bill - Amount Off",
                };
                return (
                    <span className={`whitespace-nowrap px-2 py-1 rounded-full font-medium capitalize`}>
                        {labelMap[value] || value || "—"}
                    </span>
                );
            },
        },
       ...(activeTab === "buy_x_get_y" ? [ {
            Header: t("Buy Qty"),
            accessor: "buy_qty",
            Cell: ({ row }) => (
                <span className="text-sm text-[#343C6A]">
                    {row.original.buy_qty ?? "—"}
                    {row.original.buy_unit_price
                        ? <span className="ms-1 text-[#343C6A]">@ EGP {row.original.buy_unit_price}</span>
                        : null}
                </span>
            ),
        }] : []),
        ...(activeTab === "bill_free_item" ? [{
            Header: t("Total Bill"),
            accessor: "total_bill",
            Cell: ({ row }) => (
                <span className="text-sm text-[#343C6A]">
                   {row.original.name.split(" ")[1] || "—"}
                </span>
            ),
        }]:[]),
        {
            Header: t("Free Qty"),
            accessor: "free_qty",
            Cell: ({ row }) => (
                <span className="text-sm text-[#343C6A]">
                    {row.original.free_qty ?? "—"}
                    {row.original.free_cost_price
                        ? <span className="ms-1 text-[#343C6A]">@ EGP {row.original.free_cost_price}</span>
                        : null}
                </span>
            ),
        },
        {
            Header: t("Limits"),
            id: "limits",
            Cell: ({ row }) => {
                const { max_free_per_order, max_uses_per_customer_day, max_uses_total } = row.original;
                return (
                    <div className="flex flex-col gap-0.5 text-xs  text-[#343C6A]]">
                        <span>{t("Per order")}: {max_free_per_order || t("No limit")}</span>
                        <span>{t("Per cust/day")}: {max_uses_per_customer_day || t("No limit")}</span>
                        <span>{t("Total")}: {max_uses_total || t("No limit")}</span>
                    </div>
                );
            },
        },
        {
            Header: t("Start Date"),
            accessor: "start_date",
            Cell: ({ value }) => (
                <span className="text-sm text-gray-600">{formatDate(value)}</span>
            ),
        },
        {
            Header: t("End Date"),
            accessor: "end_date",
            Cell: ({ value }) => (
                <span className="text-sm text-gray-600">{formatDate(value)}</span>
            ),
        },
        {
            Header: t("Status"),
            accessor: "status",
            Cell: ({ value }) => (
                <span className={`${STATUS_STYLES[value] || "bg-gray-100 text-gray-600" } capitalize flex justify-center font-normal text-sm rounded-xl `}
                 style={{
                  padding: "0.4rem 0.8rem",
                }}
                >
                    {value || "—"}
                </span>
            ),
        },
        {
            Header: t("Action"),
            id: "action",
            Cell: ({ row }) => (
                <div className="flex items-center gap-3">
                    <button
                        onClick={(e) => { e.stopPropagation(); onEditLine?.(row.original); }}
                    >
                        <img src={Edit} alt="edit" className="h-4 w-4" />
                    </button>
                    {showEdit && (
                        <button
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete?.(row.original); }}
                        >
                            <img src={deleteicon} alt="delete" className="h-4 w-4 mx-2" />
                        </button>
                    )}
                </div>
            ),
        },
    ], [t, showEdit, onEditLine, onDelete, activeTab]);

    const {
        headerGroups,
        prepareRow,
        page,
        nextPage,
        setPageSize,
        state: { pageSize },
    } = useTable(
        { columns, data, initialState: { pageSize: 10 } },
        useSortBy,
        usePagination,
        useRowSelect,
    );

    const [pageIndex, setPageIndex] = useState(1);

    useEffect(() => {
        const pageDetails = {
            limit: pageSize,
            offset: Math.max(0, pageSize * (pageIndex - 1)),
        };
        if (pageSize) pagination?.(pageDetails);
    }, [pageSize, pageIndex]);

    if (!flag && (!additionalData || additionalData.length === 0)) {
        return <ShimmerLoader />;
    }

    if (additionalData?.length === 0) {
        return (
            <div className="flex h-[50vh] items-center justify-center">
                <Empty description={t("No Cashier Offers Found")} />
            </div>
        );
    }

    return (
        <div className="mt-4 me-8">
            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                <table className="w-full text-sm">
                    <thead style={{ backgroundColor: "#78CFEC66" }}>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        className="px-4 py-3 text-left text-sm font-semibold text-black"
                                    >
                                        <div className="flex items-center gap-1">
                                            {column.render("Header")}
                                            {column.id !== "action" && (
                                                <img src={Vectorsorting} alt="sort" className="h-3 w-3 opacity-40" />
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody>
                        {page.map((row, i) => {
                            prepareRow(row);
                            return (
                                <tr
                                    {...row.getRowProps()}
                                    onClick={() => onRowClick?.(row.original)}
                                    className={`cursor-pointer border-t border-gray-100 transition-colors hover:bg-[#F8FAFF]
                                        ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                                >
                                    {row.cells.map((cell, index) => (
                                        <td
                                            key={index}
                                            {...cell.getCellProps()}
                                            className="px-4 py-3 text-gray-700 text-sm"
                                        >
                                            {cell.render("Cell")}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}

                        {page.length === 0 && (
                            <tr>
                                <td colSpan="100%">
                                    <Empty className="mt-10" description={t("No data available")} />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <CustomPagination
                data={data?.length}
                pageIndex={pageIndex}
                pageCount={page?.length}
                canPreviousPage={pageIndex !== 1}
                canNextPage={data?.length === pageSize}
                gotoPage={(p) => nextPage(p)}
                previousPage={() => setPageIndex((prev) => prev - 1)}
                nextPage={() => setPageIndex((prev) => prev + 1)}
                pageSize={pageSize}
                setPageSize={(size) => setPageSize(size)}
            />
        </div>
    );
};

export default CashierOfferTable;